import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'
import Breadcrumbs from 'shared/components/breadcrumbs'
import { PrimaryButton } from 'shared/components/button'
import { UserRolesEnum } from 'shared/enums/user-roles-enum'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { SaveIconAlt } from 'shared/icons/save-icon'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { shouldShowChangePasswordPage } from 'shared/utils/show-change-password-page'
import { getDashboardNotificationSettingsLink } from 'modules/header/menu/utils'
import ProfileMenuSkeleton from './profile-menu-skeleton'

interface ProfileLayoutProps {
  isFetching?: boolean
  onSave?: () => void
  disabled?: boolean
  isSaveButton?: boolean
  customHeader?: JSX.Element
  title: string
}

const MENU_ITEM_BASE_CLASS = [
  'flex border-b border-b-text-secondary/10 p-5',
  'hover:bg-brand-primary-subtle',
  'focus:outline-none focus-visible:border-b-brand-primary focus-visible:bg-brand-primary-subtle',
].join(' ')

const MENU_ITEM_ACTIVE_CLASS = [
  'border-l-4 border-l-brand-primary',
  'bg-brand-primary-subtle font-bold text-brand-primary',
].join(' ')

const MENU_ITEM_INACTIVE_CLASS = 'text-text-primary'

const ProfileLayout = ({
  isFetching,
  onSave,
  disabled,
  isSaveButton,
  customHeader,
  title,
  children,
}: React.PropsWithChildren<ProfileLayoutProps>) => {
  const { t } = useTranslation()
  const [pathName, setPathName] = useState('')
  const { user, loading } = useUser()

  const showChangePasswordPage = shouldShowChangePasswordPage(user)

  useEffect(() => {
    setPathName(window.location.pathname)
  }, [])

  if (!user || !isAuthorizedUser(user)) {
    return <div />
  }

  const hasStudentRole = user.roles.includes(UserRolesEnum.Student)
  const hasCustomerRole = user.roles.includes(UserRolesEnum.Customer)

  const breadCrumbsData = [{ caption: t('user_settings.title') }, { caption: title }]

  return (
    <>
      <div className="mb-5 flex flex-wrap justify-between gap-x-3">
        <Breadcrumbs data={breadCrumbsData} isFetching={false} />
        <div className="ml-auto flex gap-3 sm:ml-0">
          {customHeader}
          {isSaveButton && (
            <PrimaryButton
              isLoading={isFetching}
              disabled={!user || disabled}
              onClick={onSave}
              className="flex items-center justify-between"
            >
              <SaveIconAlt />
              <span className="hidden font-bold sm:flex"> {t('global.save')}</span>
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3 md:gap-5 lg:flex-row lg:gap-12">
        <div className="relative min-h-[195px] w-full lg:w-[250px]">
          <ul className="static inset-0 flex h-fit w-full flex-col overflow-auto rounded-md bg-background-secondary text-base last:border-b-transparent lg:w-[250px]">
            {loading ? (
              <ProfileMenuSkeleton />
            ) : (
              <>
                {!user.workspaceOwner && (
                  <li key="user-settings">
                    <Link
                      href="/profile/user-settings"
                      className={twMerge(
                        MENU_ITEM_BASE_CLASS,
                        pathName === '/profile/user-settings'
                          ? MENU_ITEM_ACTIVE_CLASS
                          : MENU_ITEM_INACTIVE_CLASS,
                      )}
                    >
                      {t('profile.user_settings.profile.title')}
                    </Link>
                  </li>
                )}
                {hasStudentRole && !hasCustomerRole && !user.impersonator && (
                  <li key="account-settings">
                    <Link
                      className={twMerge(
                        MENU_ITEM_BASE_CLASS,
                        pathName === '/profile/account-settings'
                          ? MENU_ITEM_ACTIVE_CLASS
                          : MENU_ITEM_INACTIVE_CLASS,
                      )}
                      href="/profile/account-settings"
                    >
                      {t('profile.user_settings.account.title')}
                    </Link>
                  </li>
                )}
                {!user.workspaceOwner && (
                  <li key="settings-subscriptions">
                    <Link
                      className={twMerge(
                        MENU_ITEM_BASE_CLASS,
                        pathName === '/profile/manage-subscriptions'
                          ? MENU_ITEM_ACTIVE_CLASS
                          : MENU_ITEM_INACTIVE_CLASS,
                      )}
                      href="/profile/manage-subscriptions"
                    >
                      {t('profile.user_settings.manage_subscriptions.title')}
                    </Link>
                  </li>
                )}
                <li key="notification-settings">
                  <Link
                    className={twMerge(
                      MENU_ITEM_BASE_CLASS,
                      pathName === '/profile/notification-settings'
                        ? MENU_ITEM_ACTIVE_CLASS
                        : MENU_ITEM_INACTIVE_CLASS,
                    )}
                    href={
                      hasCustomerRole && !isCustomDomain
                        ? getDashboardNotificationSettingsLink(user)
                        : '/profile/notification-settings'
                    }
                  >
                    {t('profile.user_settings.manage_notifications.title')}
                  </Link>
                </li>
                {showChangePasswordPage && (
                  <li key="change-passwords">
                    <Link
                      className={twMerge(
                        MENU_ITEM_BASE_CLASS,
                        pathName === '/profile/change-password'
                          ? MENU_ITEM_ACTIVE_CLASS
                          : MENU_ITEM_INACTIVE_CLASS,
                      )}
                      href="/profile/change-password"
                    >
                      {t('profile.user_settings.change_password.title')}
                    </Link>
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
        {children}
      </div>
    </>
  )
}

export default ProfileLayout
